// Dibujo del Snake en canvas. La serpiente se pinta como una línea gruesa continua que pasa por el centro
// de sus casillas; entre paso y paso la cabeza avanza y la cola recoge de forma interpolada, así el
// movimiento es fluido aunque la lógica vaya a saltos de casilla.
import type { Dir, SnakeState } from './engine';

export interface Palette {
  snake: string;
  snakeDead: string;
  food: string;
  checker: string;
  eye: string;
  pupil: string;
}

// Colores desde los tokens de index.css (cambian con el modo oscuro)
export function readPalette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, a = 1) => `rgb(${css.getPropertyValue(name).trim()} / ${a})`;
  return {
    snake: v('--game-snake'),
    snakeDead: v('--ink-3', 0.55),
    food: v('--danger'),
    checker: v('--ink', 0.035),
    eye: '#fff',
    pupil: '#1d1d1f',
  };
}

export interface Burst {
  cell: number;
  at: number; // performance.now() del bocado
}

export interface DrawInput {
  s: SnakeState;
  prev: number[];        // cuerpo antes del último paso
  alpha: number;         // 0..1: progreso hacia el siguiente paso
  cell: number;          // px por casilla
  showFood: boolean;
  burst: Burst | null;
  now: number;
  palette: Palette;
}

const DIRS: [number, number][] = [[0, -1], [1, 0], [0, 1], [-1, 0]];

export function draw(ctx: CanvasRenderingContext2D, d: DrawInput) {
  const { s, prev, cell, palette } = d;
  const { cols, rows } = s.grid;
  const W = cols * cell;
  const H = rows * cell;
  ctx.clearRect(0, 0, W, H);

  // Damero muy suave para leer la rejilla sin ruido
  ctx.fillStyle = palette.checker;
  for (let y = 0; y < rows; y++) {
    for (let x = (y % 2); x < cols; x += 2) ctx.fillRect(x * cell, y * cell, cell, cell);
  }

  const center = (i: number): [number, number] => [(i % cols + 0.5) * cell, (Math.floor(i / cols) + 0.5) * cell];
  const lerp = (a: [number, number], b: [number, number], t: number): [number, number] => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

  // Comida: late suavemente
  if (d.showFood && s.food >= 0) {
    const [fx, fy] = center(s.food);
    const pulse = 1 + Math.sin(d.now / 260) * 0.06;
    const r = cell * 0.33 * pulse;
    ctx.fillStyle = palette.food;
    ctx.beginPath();
    ctx.arc(fx, fy + cell * 0.03, r, 0, Math.PI * 2);
    ctx.fill();
    // Hoja
    ctx.fillStyle = palette.snake;
    ctx.beginPath();
    ctx.ellipse(fx + r * 0.45, fy - r * 0.95, r * 0.38, r * 0.2, -0.6, 0, Math.PI * 2);
    ctx.fill();
  }

  // Destello al comer
  if (d.burst) {
    const t = (d.now - d.burst.at) / 320;
    if (t < 1) {
      const [bx, by] = center(d.burst.cell);
      ctx.strokeStyle = palette.snake;
      ctx.globalAlpha = 1 - t;
      ctx.lineWidth = Math.max(1.5, cell * 0.08);
      ctx.beginPath();
      ctx.arc(bx, by, cell * (0.35 + t * 0.6), 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  // Puntos de la serpiente: cabeza interpolada, cuerpo del paso anterior y cola recogiéndose
  const a = d.alpha;
  const body = s.body;
  const base = prev.length ? prev : body;
  const pts: [number, number][] = [];
  const headTarget = s.alive || s.won ? body[0] : base[0];
  pts.push(lerp(center(base[0]), center(headTarget), s.alive ? a : 0));
  for (let i = 0; i < base.length - 1; i++) pts.push(center(base[i]));
  const lastPrev = base[base.length - 1];
  const beforeLast = base[base.length - 2] ?? lastPrev;
  pts.push(s.ate || !s.alive ? center(lastPrev) : lerp(center(lastPrev), center(beforeLast), a));

  const color = s.alive || s.won ? palette.snake : palette.snakeDead;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = cell * 0.68;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.stroke();

  // Cabeza algo más ancha, con ojos mirando hacia donde va
  const [hx, hy] = pts[0];
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(hx, hy, cell * 0.42, 0, Math.PI * 2);
  ctx.fill();
  drawEyes(ctx, hx, hy, s.dir, cell, palette, s.alive || s.won);
}

function drawEyes(ctx: CanvasRenderingContext2D, x: number, y: number, dir: Dir, cell: number, p: Palette, alive: boolean) {
  const [dx, dy] = DIRS[dir];
  const side: [number, number] = [-dy, dx];
  const fwd = cell * 0.14;
  const apart = cell * 0.19;
  for (const k of [-1, 1]) {
    const ex = x + dx * fwd + side[0] * apart * k;
    const ey = y + dy * fwd + side[1] * apart * k;
    ctx.fillStyle = p.eye;
    ctx.beginPath();
    ctx.arc(ex, ey, cell * 0.11, 0, Math.PI * 2);
    ctx.fill();
    if (alive) {
      ctx.fillStyle = p.pupil;
      ctx.beginPath();
      ctx.arc(ex + dx * cell * 0.035, ey + dy * cell * 0.035, cell * 0.055, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Ojos en cruz al chocar
      ctx.strokeStyle = p.pupil;
      ctx.lineWidth = Math.max(1, cell * 0.04);
      const r = cell * 0.05;
      ctx.beginPath();
      ctx.moveTo(ex - r, ey - r); ctx.lineTo(ex + r, ey + r);
      ctx.moveTo(ex + r, ey - r); ctx.lineTo(ex - r, ey + r);
      ctx.stroke();
    }
  }
}
